import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { horoscopeService, HoroscopeEntry } from '../services/horoscopeApi';

const SIGNS = [
  { name: 'Áries',       slug: 'aries',       emoji: '♈', dates: '21/03 – 19/04', element: 'Fogo', ruler: 'Marte' },
  { name: 'Touro',       slug: 'touro',        emoji: '♉', dates: '20/04 – 20/05', element: 'Terra', ruler: 'Vênus' },
  { name: 'Gêmeos',      slug: 'gemeos',       emoji: '♊', dates: '21/05 – 20/06', element: 'Ar', ruler: 'Mercúrio' },
  { name: 'Câncer',      slug: 'cancer',       emoji: '♋', dates: '21/06 – 22/07', element: 'Água', ruler: 'Lua' },
  { name: 'Leão',        slug: 'leao',         emoji: '♌', dates: '23/07 – 22/08', element: 'Fogo', ruler: 'Sol' },
  { name: 'Virgem',      slug: 'virgem',       emoji: '♍', dates: '23/08 – 22/09', element: 'Terra', ruler: 'Mercúrio' },
  { name: 'Libra',       slug: 'libra',        emoji: '♎', dates: '23/09 – 22/10', element: 'Ar', ruler: 'Vênus' },
  { name: 'Escorpião',   slug: 'escorpiao',    emoji: '♏', dates: '23/10 – 21/11', element: 'Água', ruler: 'Plutão' },
  { name: 'Sagitário',   slug: 'sagitario',    emoji: '♐', dates: '22/11 – 21/12', element: 'Fogo', ruler: 'Júpiter' },
  { name: 'Capricórnio', slug: 'capricornio',  emoji: '♑', dates: '22/12 – 19/01', element: 'Terra', ruler: 'Saturno' },
  { name: 'Aquário',     slug: 'aquario',      emoji: '♒', dates: '20/01 – 18/02', element: 'Ar', ruler: 'Urano' },
  { name: 'Peixes',      slug: 'peixes',       emoji: '♓', dates: '19/02 – 20/03', element: 'Água', ruler: 'Netuno' },
];

const elementColor: Record<string, string> = {
  Fogo: 'from-orange-500/20 to-red-500/20 border-orange-500/30',
  Terra: 'from-green-500/20 to-emerald-500/20 border-green-500/30',
  Ar: 'from-sky-500/20 to-blue-500/20 border-sky-500/30',
  Água: 'from-indigo-500/20 to-violet-500/20 border-indigo-500/30',
};

const PublicHoroscopePage: React.FC = () => {
  const { sign: signSlug } = useParams<{ sign: string }>();
  const navigate = useNavigate();

  const signInfo = SIGNS.find(s => s.slug === signSlug) || SIGNS[0];

  const [horoscope, setHoroscope] = useState<HoroscopeEntry | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    document.title = `Horóscopo de ${signInfo.name} hoje — Astralyz`;
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.setAttribute('content',
        `Horóscopo de ${signInfo.name} para hoje. Leia as previsões astrologicas do dia para ${signInfo.name} geradas por inteligência artificial no Astralyz.`
      );
    }
    fetchHoroscope();
    return () => { document.title = 'Astralyz — Tarot & Astrologia'; };
  }, [signInfo.name]);

  const fetchHoroscope = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await horoscopeService.getToday(signInfo.name);
      setHoroscope(data);
    } catch {
      setError('Não foi possível carregar o horóscopo agora.');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (iso: string) => {
    const [y, m, d] = iso.split('-');
    return `${d}/${m}/${y}`;
  };

  const colorClass = elementColor[signInfo.element] || elementColor['Fogo'];

  return (
    <div className="min-h-screen bg-gradient-mystical">
      {/* Header simples */}
      <header className="border-b border-white/10 bg-black/20 backdrop-blur-sm">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="text-white/60 hover:text-white transition-colors text-sm font-mystical tracking-widest uppercase">
            Astralyz
          </Link>
          <Link to="/register" className="text-xs px-4 py-2 rounded-full border border-white/20 text-white/70 hover:border-white/50 hover:text-white transition-all">
            Criar conta grátis
          </Link>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 py-10">
        {/* Seletor de signos */}
        <div className="flex flex-wrap justify-center gap-2 mb-10">
          {SIGNS.map(s => (
            <button
              key={s.slug}
              onClick={() => navigate(`/horoscope/${s.slug}`)}
              className={`px-3 py-1.5 rounded-full text-sm transition-all ${
                s.slug === signSlug
                  ? 'bg-mystical-600 text-white font-semibold'
                  : 'bg-white/5 text-white/60 hover:bg-white/10 hover:text-white'
              }`}
            >
              {s.emoji} {s.name}
            </button>
          ))}
        </div>

        {/* Cabeçalho do signo */}
        <div className="text-center mb-8">
          <div className="text-7xl sm:text-8xl mb-4">{signInfo.emoji}</div>
          <h1 className="text-3xl sm:text-5xl font-mystical font-bold text-white mb-2">
            Horóscopo de {signInfo.name}
          </h1>
          <p className="text-white/40 text-sm">{signInfo.dates}</p>
          <div className="flex justify-center gap-4 mt-3">
            <span className="text-xs text-white/40 bg-white/5 px-3 py-1 rounded-full">
              Elemento: {signInfo.element}
            </span>
            <span className="text-xs text-white/40 bg-white/5 px-3 py-1 rounded-full">
              Regente: {signInfo.ruler}
            </span>
          </div>
        </div>

        {/* Conteúdo do horóscopo */}
        {loading && (
          <div className="flex flex-col items-center py-16 gap-4">
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}
              className="text-4xl"
            >
              ✦
            </motion.div>
            <p className="text-white/40 text-sm">Aurora está consultando os astros…</p>
          </div>
        )}

        {error && !loading && (
          <div className="text-center py-12">
            <p className="text-white/50 mb-4">{error}</p>
            <button
              onClick={fetchHoroscope}
              className="px-6 py-2 bg-mystical-600 text-white rounded-full text-sm hover:bg-mystical-700 transition-colors"
            >
              Tentar novamente
            </button>
          </div>
        )}

        {horoscope && !loading && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div className={`bg-gradient-to-br ${colorClass} backdrop-blur-sm rounded-2xl p-6 sm:p-8 border mb-6`}>
              <div className="flex items-center justify-between mb-4">
                <span className="text-xs text-white/40 uppercase tracking-widest">Previsão do dia</span>
                <span className="text-xs text-white/40">{formatDate(horoscope.date)}</span>
              </div>
              <p className="text-white/90 text-base sm:text-lg leading-relaxed whitespace-pre-line">
                {horoscope.content}
              </p>
            </div>

            {/* CTA */}
            <div className="text-center rounded-2xl p-6 sm:p-8 bg-white/5 border border-white/10">
              <p className="text-white/50 text-sm mb-2">Com carinho por Aurora Lyz</p>
              <h2 className="text-white font-mystical font-bold text-xl sm:text-2xl mb-3">
                Quer uma consulta personalizada?
              </h2>
              <p className="text-white/50 text-sm mb-6 max-w-sm mx-auto">
                Tarot, mapa astral, sinastria e numerologia com a Aurora como sua guia espiritual.
              </p>
              <Link
                to="/register"
                className="inline-block px-8 py-3 rounded-full text-white font-bold text-base transition-opacity hover:opacity-90 mb-3"
                style={{ background: 'linear-gradient(135deg, #7c3aed, #a78bfa)' }}
              >
                ✨ Criar conta grátis
              </Link>
              <p className="text-white/30 text-xs mt-3">
                Já tem conta?{' '}
                <Link to="/login" className="text-gold-400 hover:text-gold-300 transition-colors">
                  Entrar
                </Link>
              </p>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default PublicHoroscopePage;
